import { useEffect, useState } from 'react';
import { SymbolWahl, SYMBOL_WAEHLEN } from '@/components/auth/SymbolWahl';
import { NutzerChip } from '@/components/ui/NutzerChip';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { CircleHelp, ExternalLink, LogOut, Plus, Settings, Users, X, Palette } from 'lucide-react';
import { useSitzungsWechsel } from '@/hooks/useSitzungsWechsel';
import { useSettings } from '@/hooks/useSettings';
import { sessionLabel, userLabel } from '@/lib/format';
import { portalAdresse } from '@/lib/portal';
import { registriereMobilMenue } from '@/lib/mobilMenue';
import { faengtAussenklickAb } from '@/onboarding/tourSchutz';
import { HilfePanel } from '@/onboarding/HilfePanel';
import {
  NutzerHinzufuegen,
  NUTZER_HINZUFUEGEN
} from '@/components/auth/NutzerHinzufuegen';
import { C } from '@/theme';
import { FOKUS_RING } from '@/lib/fokus';

// Das Menü der Bottom-Navigation.
//
// Am Schreibtisch stehen Hilfe und Nutzerwechsel dauerhaft im Bild — in
// der Seitenspalte und in der Kopfzeile. Auf dem Telefon ist dafür kein
// Platz, ohne den Daumenbereich zu verstopfen. Beides liegt deshalb hier,
// einen Griff entfernt.
//
// „Einstellungen" steht seit A54 (#146) drin. Vorher fehlte der Punkt
// mit der Begründung, die Seite gebe es noch nicht (SPEC-008) — sie gibt
// es inzwischen, mit fünf Abschnitten, und die Seitenspalte führt über
// Sidebar.tsx:36 hin. Auf dem Telefon führte nichts hin: „die
// Einstellungen lassen sich übers Handy nicht öffnen."
//
// Er steht direkt hinter dem Trennstrich, vor Hilfe und Portal. Der
// Strich trennt „wer bin ich" von „womit arbeite ich"; dahinter läuft
// dieselbe Reihenfolge wie in der Seitenspalte, wo Einstellungen das
// letzte Navigationsziel ist und Hilfe und Portal darunter folgen.
//
// Die Bottom-Leiste kam dafür nicht in Frage: Sie hält fünf Ziele, und
// fünf ist dort die Grenze (BottomNav.tsx:14). Ein sechstes müsste ins
// Menü — genau hier.

function Punkt({
  icon: Icon,
  label,
  hinweis,
  farbe,
  chip,
  anker,
  onClick
}: {
  icon?: typeof CircleHelp;
  label: string;
  hinweis?: string;
  farbe?: string | null;
  /** Identitäts-Chip statt Farbpunkt (A9.1) — nur bei Nutzer-Einträgen. */
  chip?: { symbol: string | null; kuerzel: string; name: string };
  anker?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-tour={anker}
      onClick={onClick}
      className="flex min-h-[52px] w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-ink hover:bg-cream-dark"
    >
      {chip !== undefined ? (
        <NutzerChip
          farbe={farbe ?? null}
          symbol={chip.symbol}
          kuerzel={chip.kuerzel}
          name={chip.name}
          groesse="gross"
        />
      ) : farbe !== undefined ? (
        <span
          aria-hidden
          className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: farbe ?? C.inkFaint }}
        />
      ) : (
        Icon && <Icon className="h-5 w-5 shrink-0 text-ink-soft" />
      )}
      <span className="flex min-w-0 flex-col">
        <span className="truncate">{label}</span>
        {hinweis && <span className="truncate text-xs text-ink-faint">{hinweis}</span>}
      </span>
    </button>
  );
}

export function MobilMenue() {
  const navigate = useNavigate();
  const [offen, setOffen] = useState(false);
  const [hilfeOffen, setHilfeOffen] = useState(false);
  const [symbolWahl, setSymbolWahl] = useState(false);
  const { me, andere, wechseln, abmelden, hinzufuegen, nutzerHinzufuegen } =
    useSitzungsWechsel();
  // Ohne nc_url: die Anleitung holt ihre Adresse jetzt selbst über den
  // Hook. Damit trägt diese Abfrage denselben Schlüssel wie die der
  // Seitenspalte, statt wegen eines Schlüssels mehr einen eigenen
  // Cache-Eintrag zu belegen.
  const settings = useSettings(['portal_url', 'portal_label']);
  // Geprüft statt geglaubt — siehe lib/portal.ts. Ist die Adresse
  // unbrauchbar, entfällt der Eintrag.
  const portalUrl = portalAdresse(settings.data?.settings.portal_url);
  const portalLabel = settings.data?.settings.portal_label ?? null;

  // Die Einführungstour zeigt auf Nutzerwechsel und Hilfe. Beide liegen
  // hier drin — sie muss das Menü also aufziehen können, ohne diesen
  // Zustand zu kennen (siehe lib/mobilMenue.ts).
  useEffect(
    () =>
      registriereMobilMenue({
        oeffnen: () => setOffen(true),
        schliessen: () => setOffen(false)
      }),
    []
  );

  return (
    <>
      <Dialog.Root open={offen} onOpenChange={setOffen}>
        <Dialog.Trigger asChild>
          <button
            type="button"
            data-tour="mobil-menue"
            aria-label="Menü"
            className={`flex min-h-[56px] min-w-[56px] flex-1 flex-col items-center justify-center gap-0.5 rounded-md px-2 py-1 text-ink-soft hover:bg-cream-dark hover:text-akzent ${FOKUS_RING}`}
          >
            {/* Der Farbpunkt statt eines Hamburgers: am gemeinsamen Gerät
                ist „in wessen Namen arbeite ich gerade“ die Frage, die
                der Knopf beantwortet — noch bevor man ihn drückt. */}
            {/* A9.1: Der Chip statt des Farbpunkts — dasselbe Signal,
                nur benennbar. */}
            <span className="inline-flex h-6 w-6 items-center justify-center">
              {me === null ? (
                <span
                  aria-hidden
                  className="inline-block h-3 w-3 rounded-full"
                  style={{ backgroundColor: C.inkFaint }}
                />
              ) : (
                <NutzerChip
                  farbe={me.farbe}
                  symbol={me.symbol}
                  kuerzel={me.kuerzel}
                  name={userLabel(me)}
                  groesse="klein"
                />
              )}
            </span>
            <span className="text-[0.65rem] leading-none">Menü</span>
          </button>
        </Dialog.Trigger>

        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[70] bg-ink/30" />
          <Dialog.Content
            // Die Tour hängt am <body>: ohne diese Zeile gilt ein Klick in
            // die Sprechblase als Klick daneben und schliesst das Menü.
            onPointerDownOutside={faengtAussenklickAb}
            onInteractOutside={faengtAussenklickAb}
            aria-describedby={undefined}
            data-ueber-tour=""
            // Von unten, nicht bildschirmfüllend: ein Menü ist ein Griff,
            // keine Ansicht. Die Sicherheitszone des Home-Indikators kommt
            // unten dazu, sonst liegt der letzte Punkt darunter.
            className="fixed inset-x-0 bottom-0 z-[70] max-h-[85vh] overflow-y-auto rounded-t-xl border-t border-line bg-cream pb-[env(safe-area-inset-bottom)] shadow-lg"
          >
            <div className="flex items-center justify-between px-4 pt-4">
              <Dialog.Title className="font-serif text-lg text-ink">Menü</Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Menü schließen"
                  className="inline-flex h-11 w-11 items-center justify-center rounded text-ink-soft hover:bg-cream-dark"
                >
                  <X className="h-5 w-5" />
                </button>
              </Dialog.Close>
            </div>

            <div className="space-y-1 p-3">
              {me && (
                <div data-tour="benutzermenue" className="space-y-1">
                  <p className="px-3 pt-1 text-xs uppercase tracking-wide text-ink-faint">
                    Angemeldet
                  </p>
                  <div className="flex min-h-[52px] items-center gap-3 rounded-md bg-akzent-sanft px-3 py-2 text-sm">
                    <NutzerChip
                      farbe={me.farbe}
                      symbol={me.symbol}
                      kuerzel={me.kuerzel}
                      name={userLabel(me)}
                      groesse="gross"
                    />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-ink">{userLabel(me)}</span>
                      <span className="truncate text-xs text-ink-faint">{me.nc_username}</span>
                    </span>
                  </div>

                  {andere.map((s) => (
                    <Punkt
                      key={s.username}
                      label={sessionLabel(s)}
                      hinweis="Zu dieser Sitzung wechseln"
                      farbe={s.farbe}
                      chip={{ symbol: s.symbol, kuerzel: s.kuerzel, name: sessionLabel(s) }}
                      onClick={() => {
                        setOffen(false);
                        void wechseln(s.username);
                      }}
                    />
                  ))}

                  {/* Führt nicht mehr stur in den Anmeldeablauf: auf
                      diesem Gerät kommt dort ohne Zwischenschritt immer
                      derselbe Nextcloud-Nutzer an (K5). Das Panel
                      erklärt, warum, und bietet beide Schritte an.

                      Seit P4 entscheidet das der gemeinsame Hook, nicht
                      mehr diese Ansicht — der Schreibtisch-Weg hatte den
                      Schritt sonst nie bekommen. */}
                  {/* A9.1: Dasselbe Angebot wie am Schreibtisch — sonst
                      liefen die beiden Rahmen wieder auseinander (P4). */}
                  <Punkt
                    icon={Palette}
                    label={SYMBOL_WAEHLEN}
                    onClick={() => {
                      setOffen(false);
                      setSymbolWahl(true);
                    }}
                  />
                  <Punkt
                    icon={Plus}
                    label={NUTZER_HINZUFUEGEN}
                    onClick={() => {
                      setOffen(false);
                      hinzufuegen();
                    }}
                  />
                  <Punkt
                    icon={LogOut}
                    label="Abmelden"
                    onClick={() => {
                      setOffen(false);
                      void abmelden('current');
                    }}
                  />
                  <Punkt
                    icon={Users}
                    label="Alle abmelden"
                    onClick={() => {
                      setOffen(false);
                      void abmelden('all');
                    }}
                  />
                </div>
              )}

              <div className="my-1 h-px bg-line" />

              <Punkt
                icon={Settings}
                label="Einstellungen"
                hinweis="Mitarbeiter, Wege, Versandarten, Checkliste"
                onClick={() => {
                  // Erst zu, dann hin — wie bei der Hilfe darunter. Ein
                  // offenes Menü über der Zielseite wäre eine Ebene, die
                  // niemand geschlossen hat.
                  setOffen(false);
                  void navigate({ to: '/einstellungen' });
                }}
              />

              <Punkt
                icon={CircleHelp}
                label="Hilfe"
                anker="hilfe"
                hinweis="Einführungstour und Kapitel zum Nachschlagen"
                onClick={() => {
                  // Erst zu, dann auf: zwei Ebenen übereinander wären auf
                  // einem Telefonbildschirm eine Ebene zu viel.
                  setOffen(false);
                  setHilfeOffen(true);
                }}
              />

              {/* Ein Auswärts-Link, und er sieht auch so aus.
                  Vorher trug er einen Pfeil nach links — das Symbol für
                  „zurück". Wer darauf tippte, erwartete das Dashboard und
                  landete auf einer fremden Seite; stimmte die Adresse
                  nicht, war es eine 404 ohne Rückweg, weil die
                  installierte App keine Adressleiste hat.

                  Deshalb jetzt ein echtes <a> mit target="_blank": das
                  Fenster der App bleibt stehen, wo es war. */}
              {portalUrl && (
                <a
                  href={portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOffen(false)}
                  className="flex min-h-[52px] w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-ink hover:bg-cream-dark"
                >
                  <ExternalLink className="h-5 w-5 shrink-0 text-ink-soft" />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{portalLabel ?? 'Portal'}</span>
                    <span className="truncate text-xs text-ink-faint">
                      Öffnet sich außerhalb des Trackings
                    </span>
                  </span>
                </a>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {hilfeOffen && <HilfePanel open onClose={() => setHilfeOffen(false)} />}

      <NutzerHinzufuegen {...nutzerHinzufuegen} />

      {/* Erst mounten, wenn offen — dieselbe Begründung wie im
          Schreibtisch-Menü. */}
      {symbolWahl && me !== null && (
        <SymbolWahl
          open
          onClose={() => setSymbolWahl(false)}
          farbe={me.farbe}
          symbol={me.symbol}
          kuerzel={me.kuerzel}
        />
      )}
    </>
  );
}
